import { useCallback } from 'react';

import { useAppStore, setAppStore, type IAppStore } from '@/store';
import { useBrowserHistory } from '@/hooks/useBrowserHistory';
import { resetCanvasLoadState } from '@/utils/canvas/canvasLoadState';
import { closeMiniapp } from '@/utils/editor/closeAndNavigate';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import getScreenType from '@/utils/analytics/getScreenType';
import { AiGenScreenAction } from '@/types/analytics';
import { Screen } from '@/types';

interface UseAppNavigationOptions {
  onBackFromSettings: () => void;
  onBackFromProfile: () => void;
  onBackFromCredits: () => void;
}

const selector = (store: IAppStore) => ({
  screen: store.screen,
  setScreen: store.setScreen,
});

/**
 * Wires the browser back-button (Android WebView + browser) to the in-app
 * navigation. Each back handler is a single concern: analytics + state.
 *
 * The lightbox-close handler still uses a window event because Generations
 * owns its lightbox state locally — that coupling is tracked separately
 * (audit issue: replace the custom event with a store flag).
 */
const useAppNavigation = ({
  onBackFromSettings,
  onBackFromProfile,
  onBackFromCredits,
}: UseAppNavigationOptions) => {
  const { screen, setScreen } = useAppStore(selector);

  const onBackToLibrary = useCallback(() => {
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.BACK,
      screen_type: getScreenType(),
    });
    resetCanvasLoadState();
    setScreen(Screen.Library);
  }, [setScreen]);

  const onBackFromFiles = useCallback(() => {
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.BACK,
      screen_type: getScreenType(),
    });
    setScreen(Screen.Library);
  }, [setScreen]);

  const onBackFromLightbox = useCallback(() => {
    window.dispatchEvent(new Event('backbutton:close-lightbox'));
  }, []);

  const onBackFromSeeAll = useCallback(() => {
    setAppStore({ seeAllCategorySlug: null });
  }, []);

  const onCloseApp = useCallback(() => {
    try {
      closeMiniapp();
    } catch {
      // SDK may not be available
    }
  }, []);

  useBrowserHistory({
    screen,
    onBackToLibrary,
    onBackFromFiles,
    onBackFromLightbox,
    onBackFromSettings,
    onBackFromProfile,
    onBackFromCredits,
    onBackFromSeeAll,
    onCloseApp,
  });
};

export default useAppNavigation;
